import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CopyIcon, EllipsisVerticalIcon, PencilIcon, SearchIcon } from 'lucide-react'
import {
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	EmptyState,
	IconButton,
	IllustrationEmpty,
	Input,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import {
	useGetRentalAutomationQuery,
	useGetRentalQuery,
	useListContactsQuery,
	useListPropertiesQuery,
	useListRentalsQuery,
} from '@/api'
import type { Rental, RentalStatus } from '@/types'
import { formatPhone } from '@/lib/phone'
import { formatPropertyAddress } from '@/lib/property-address'
import { CURRENCY_SIGNS, DEFAULT_LOCALE } from '@/types'
import { AutomationSetupDialog } from '@/components/automation-setup-dialog'
import { DuplicateAutomationDialog } from '@/components/duplicate-automation-dialog'
import { StartRentalDialog } from '@/components/start-rental-dialog'
import { useCan } from '@/lib/access'

export const Route = createFileRoute('/_app/automation')({
	component: AutomationPage,
})

const STATUS_TABS: { value: RentalStatus | 'ALL'; labelKey: string }[] = [
	{ value: 'ALL',      labelKey: 'rent.automation.page.tabs.all' },
	{ value: 'ACTIVE',   labelKey: 'rent.automation.page.tabs.active' },
	{ value: 'UPCOMING', labelKey: 'rent.automation.page.tabs.upcoming' },
]

function formatDate(iso?: string) {
	if (!iso) return '—'
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatMoney(amount?: string, currency?: string) {
	if (!amount) return '—'
	const num = Number(amount)
	const sign = CURRENCY_SIGNS[currency ?? ''] ?? currency ?? ''
	return `${Number.isFinite(num) ? num.toLocaleString(DEFAULT_LOCALE) : amount} ${sign}`.trim()
}

// ── Per-row actions ─────────────────────────────────────────

interface RowActionsProps {
	rental: Rental
	onSetup:     () => void
	onDuplicate: () => void
}

function RowActions({ rental, onSetup, onDuplicate }: RowActionsProps) {
	const canSetUp = useCan(['create', 'update'], 'RentalAutomation')
	const { t } = useTranslation()
	const navigate = useNavigate()

	return (
		<div className="flex items-center justify-end gap-1">
			{canSetUp && (
				<Button
					variant="secondary"
					size="sm"
					onClick={onSetup}
				>
					{t('rent.automation.page.row.configure', { defaultValue: 'Настроить' })}
				</Button>
			)}
			<DropdownMenu>
				<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label="Меню" />}>
					<EllipsisVerticalIcon className="size-4" />
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="min-w-44">
					<DropdownMenuItem onClick={() => navigate({ to: '/rent/$rentalId', params: { rentalId: rental.id } })}>
						<PencilIcon />
						{t('rent.automation.page.row.openRental', { defaultValue: 'Открыть договор' })}
					</DropdownMenuItem>
					{canSetUp && (
						<DropdownMenuItem onClick={onDuplicate}>
							<CopyIcon />
							{t('rent.automation.actions.duplicate', { defaultValue: 'Продублировать' })}
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}

// ── Lazy loader: setup dialog for a rental row ──────────────
//
// Fetches rental + its automation on demand (only when user clicks "Настроить").

interface SetupOrchestratorProps {
	rentalId: string
	onClose:  () => void
}

function SetupOrchestrator({ rentalId, onClose }: SetupOrchestratorProps) {
	const { data: rental }     = useGetRentalQuery(rentalId)
	const { data: automation } = useGetRentalAutomationQuery(rentalId)

	if (!rental) return null

	return (
		<AutomationSetupDialog
			open
			onOpenChange={(v) => { if (!v) onClose() }}
			rentalId={rentalId}
			rental={rental}
			initial={automation ?? undefined}
		/>
	)
}

// ── Lazy automation loader for duplication ──────────────────
//
// Mounted only when the user clicks "Продублировать" on a row.
// Fetches source automation, then opens AutomationSetupDialog for the target.

interface DuplicateOrchestratorProps {
	sourceRentalId: string
	targetRentalId: string
	targetRental:   Rental
	onClose:        () => void
}

function DuplicateOrchestrator({ sourceRentalId, targetRentalId, targetRental, onClose }: DuplicateOrchestratorProps) {
	const { data: sourceAutomation, isLoading } = useGetRentalAutomationQuery(sourceRentalId)

	if (isLoading) return null

	return (
		<AutomationSetupDialog
			open
			onOpenChange={(v) => { if (!v) onClose() }}
			rentalId={targetRentalId}
			rental={targetRental}
			initial={sourceAutomation ?? undefined}
		/>
	)
}

// ── Page ────────────────────────────────────────────────────

function AutomationPage() {
	const { t } = useTranslation()
	const canAddRental = useCan('create', 'Rental')

	const [statusFilter, setStatusFilter] = useState<RentalStatus | 'ALL'>('ALL')
	const [search, setSearch]             = useState('')

	const [setupRentalId, setSetupRentalId]   = useState<string | null>(null)
	const [addRentalOpen, setAddRentalOpen]   = useState(false)

	// Source rental for duplication (the row the user clicked Продублировать on)
	const [sourceRentalId, setSourceRentalId] = useState<string | null>(null)
	// Opens DuplicateAutomationDialog (pick target property → rental)
	const [duplicateOpen, setDuplicateOpen]   = useState(false)
	// After target is picked, store it here to launch DuplicateOrchestrator
	const [duplicateTarget, setDuplicateTarget] = useState<{ rentalId: string; rental: Rental } | null>(null)

	const { data, isLoading } = useListRentalsQuery(
		statusFilter === 'ALL'
			? { limit: 200 }
			: { status: statusFilter, limit: 200 },
	)
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const { data: contactsData }   = useListContactsQuery({ type: 'TENANT', limit: 200 })

	const propertiesById = useMemo(() => {
		const map = new Map<string, { title: string; address?: string; image?: string }>()
		for (const p of propertiesData?.properties ?? []) {
			map.set(p.id, {
				title:   p.address,
				address: formatPropertyAddress(p.city, p.address) || undefined,
				image:   p.images?.[0]?.url,
			})
		}
		return map
	}, [propertiesData])

	const contactsById = useMemo(() => {
		const map = new Map<string, string>()
		for (const c of contactsData?.items ?? []) {
			map.set(c.id, c.name ?? (formatPhone(c.phone) || '—'))
		}
		return map
	}, [contactsData])

	const rentals = data?.rentals ?? []

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		if (!q) return rentals
		return rentals.filter((r) => {
			const prop = propertiesById.get(r.propertyId)
			const tenant = r.tenantContactId ? contactsById.get(r.tenantContactId) : undefined
			const hay = `${prop?.address ?? ''} ${tenant ?? ''}`.toLowerCase()
			return hay.includes(q)
		})
	}, [rentals, search, propertiesById, contactsById])

	function handleDuplicate(rentalId: string) {
		setSourceRentalId(rentalId)
		setDuplicateOpen(true)
	}

	function handleDuplicateTargetPicked(targetRentalId: string, targetRental: Rental) {
		setDuplicateOpen(false)
		setDuplicateTarget({ rentalId: targetRentalId, rental: targetRental })
	}

	function handleDuplicateClose() {
		setDuplicateTarget(null)
		setSourceRentalId(null)
	}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			{/* Header */}
			<div className="flex flex-col gap-4 border-b border-(--color-border) px-6 py-5">
				<h1 className="text-heading-2xl">
					{t('nav.automation', { defaultValue: 'Автоматизация' })}
				</h1>

				<div className="flex items-center gap-3">
					<Tabs
						value={statusFilter}
						onValueChange={(v) => setStatusFilter(v as RentalStatus | 'ALL')}
					>
						<TabsList variant="line">
							{STATUS_TABS.map(({ value, labelKey }) => (
								<TabsTrigger key={value} variant="line" value={value}>
									{t(labelKey, { defaultValue: value })}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>

					<div className="relative ml-auto w-64">
						<SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-muted-foreground)" />
						<Input
							placeholder={t('rent.automation.page.searchPlaceholder', { defaultValue: 'Поиск по объекту или арендатору…' })}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="flex-1 overflow-auto">
				{isLoading ? (
					<div className="flex h-full items-center justify-center">
						<Spinner />
					</div>
				) : filtered.length === 0 ? (
					<EmptyState
						illustration={<IllustrationEmpty />}
						title={t('rent.automation.page.emptyTitle', { defaultValue: 'Нет договоров аренды' })}
						description={t('rent.automation.page.emptyDescription', { defaultValue: 'Добавьте первый договор аренды, чтобы настроить автоматизацию' })}
						actionSlot={canAddRental && (
							<Button onClick={() => setAddRentalOpen(true)}>
								{t('rent.addRental')}
							</Button>
						)}
					/>
				) : (
					<Table className="table-fixed">
						<TableHeader>
							<TableRow className="hover:bg-transparent">
								<TableHead className="text-label-medium text-(--color-muted-foreground)">
									{t('rent.automation.page.columns.property', { defaultValue: 'Объект' })}
								</TableHead>
								<TableHead className="w-48 text-label-medium text-(--color-muted-foreground)">
									{t('rent.automation.page.columns.tenant', { defaultValue: 'Арендатор' })}
								</TableHead>
								<TableHead className="w-44 text-label-medium text-(--color-muted-foreground)">
									{t('rent.automation.page.columns.period', { defaultValue: 'Период' })}
								</TableHead>
								<TableHead className="w-36 text-label-medium text-(--color-muted-foreground)">
									{t('rent.automation.page.columns.monthly', { defaultValue: 'Ежемесячно' })}
								</TableHead>
								<TableHead className="w-44" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((rental) => {
								const prop   = propertiesById.get(rental.propertyId)
								const tenant = rental.tenantContactId ? contactsById.get(rental.tenantContactId) : undefined
								return (
								<TableRow key={rental.id} className="h-[68px]">
									<TableCell className="px-4">
										<div className="flex items-center gap-3">
											{prop?.image ? (
												<img src={prop.image} alt={prop.title} className="size-12 shrink-0 rounded-xl object-cover" />
											) : (
												<div className="size-12 shrink-0 rounded-xl bg-(--color-muted)" />
											)}
											<div className="flex min-w-0 flex-col gap-1">
												<span className="truncate text-label-medium text-(--color-foreground)">
													{prop?.title ?? '—'}
												</span>
												{prop?.address && (
													<span className="truncate text-label-normal text-(--color-muted-foreground)">
														{prop.address}
													</span>
												)}
											</div>
										</div>
									</TableCell>
									<TableCell className="w-48 px-4">
										{rental.tenantContactId ? (
											<span className="truncate text-label-normal text-(--color-foreground)">
												{tenant ?? `Арендатор №${rental.tenantContactId.slice(-6)}`}
											</span>
										) : (
											<span className="text-label-normal text-(--color-muted-foreground)">—</span>
										)}
									</TableCell>
									<TableCell className="w-44 px-4 text-label-normal text-(--color-muted-foreground)">
										{formatDate(rental.startDate)} — {formatDate(rental.endDate)}
									</TableCell>
									<TableCell className="w-36 px-4 text-label-normal text-(--color-foreground)">
										{formatMoney(rental.rentAmount, rental.currency)}
									</TableCell>
									<TableCell className="w-44 px-4">
										<RowActions
											rental={rental}
											onSetup={() => setSetupRentalId(rental.id)}
											onDuplicate={() => handleDuplicate(rental.id)}
										/>
									</TableCell>
								</TableRow>
							)})}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Dialogs */}
			<StartRentalDialog open={addRentalOpen} onOpenChange={setAddRentalOpen} />

			{setupRentalId && (
				<SetupOrchestrator
					rentalId={setupRentalId}
					onClose={() => setSetupRentalId(null)}
				/>
			)}

			<DuplicateAutomationDialog
				open={duplicateOpen}
				onOpenChange={(v) => {
					setDuplicateOpen(v)
					if (!v) setSourceRentalId(null)
				}}
				onRentalPicked={handleDuplicateTargetPicked}
			/>

			{duplicateTarget && sourceRentalId && (
				<DuplicateOrchestrator
					sourceRentalId={sourceRentalId}
					targetRentalId={duplicateTarget.rentalId}
					targetRental={duplicateTarget.rental}
					onClose={handleDuplicateClose}
				/>
			)}
		</div>
	)
}
